<template>
  <view-wrapper name="Labels" :loading="loading" :loadingText="loadingText">
    <router-link
      class="mt-3 mb-12 button big primary white"
      to="addlabel"
      v-if="disableCreateNewLabel"
      >Create new label
    </router-link>
    <modal
      v-if="showWarning"
      @close="closeWarningModal"
      type="warning"
      heading="Warning"
      :description="warningText"
    >
      <template slot="body">
        <h3>Warning</h3>
        <p class="m-0">{{ warningText }}</p>
        <div class="mt-4 flex items-center justify-center">
          <label class="flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              v-model="dontShowAgain"
              class="mr-2 w-4 h-4 text-primary-light border-gray-300 rounded focus:ring-primary-light"
            />
            <span class="text-sm font-medium text-primary-dark">Don't show this again</span>
          </label>
        </div>
      </template>
    </modal>

    <modal
      v-if="confirmDeleteOpen"
      @close="confirmDeleteOpen = !confirmDeleteOpen"
      type="warning"
    >
      <template slot="body">
        <h3>Are you sure?</h3>
        <p>This action cannot be reverted</p>
      </template>
      <template slot="buttons">
        <grafokett-button
          class="mb-2 button big primary white"
          @click="confirmDeleteOpen = !confirmDeleteOpen"
          >Cancel
        </grafokett-button>
        <grafokett-button
          class="mb-2 button big primary white"
          @click="deleteLabel()"
          >Delete
        </grafokett-button>
      </template>
    </modal>

    <expandabel-table
      :text="'Active labels'"
      :columnsToNotSort="columnsToNotSort"
      :data="labels"
      :headers="headers"
      v-slot:child="{ child }"
      @searchQuery="activeSearchText = $event"
    >
      <div class="text-right">
        <router-link :to="`labels/${child.parentId}`" class="button small"
          >Edit <i class="text-lg edit-symbol"
        /></router-link>
        <grafokett-button class="small" @click="openConfirmModal(child)"
          >Delete
        </grafokett-button>
      </div>
      <p class="mb-5">Variabels</p>
      <div v-for="(v, index) in child.variabels" :key="'var' + index">
        <div>
          <p class="flex-1">{{ v.name }}</p>
          <div class="flex p-3 rounded bg-primary-light">
            <span class="flex-1">{{ v.boundToName }}</span>
            <i class="p-1 lock-symbol" :class="!v.locked ? '' : 'locked'" />
          </div>
        </div>
      </div>
      <p v-if="child.groups.length > 0" class="my-5">Assigned to groups</p>
      <div class="rounded bg-primary-light">
        <div
          class="p-4 border-0 border-solid border-gray"
          :class="index !== 0 ? 'border-t' : ''"
          v-for="(g, index) in child.groups"
          :key="'var' + index"
        >
          <span>{{ g.name }}</span>
        </div>
      </div>
    </expandabel-table>

    <expandabel-table
      :text="'Inactive labels'"
      :columnsToNotSort="columnsToNotSort"
      :data="disabledLabels"
      :headers="headers"
      @searchQuery="disabledSearchText = $event"
    >
      <template v-slot:column="{ column, child }">
        <div class="text-primary-light">
          {{ child[column] }}
        </div>
      </template>
      <template v-slot:child="{ child }">
        <div class="text-right">
          <router-link :to="`labels/${child.parentId}`" class="button small"
            >Edit <i class="text-lg edit-symbol"
          /></router-link>
          <grafokett-button class="small" @click="openConfirmModal(child)"
            >Delete
          </grafokett-button>
        </div>
        <div class="text-primary-light">
          <p class="mb-5">Variabels</p>
          <div v-for="(v, index) in child.variabels" :key="'var' + index">
            <div>
              <p class="flex-1">{{ v.name }}</p>
              <div class="flex p-3 rounded bg-primary-light">
                <span class="flex-1 text-primary">{{ v.boundToName }}</span>
                <i class="p-1 lock-symbol" :class="!v.locked ? '' : 'locked'" />
              </div>
            </div>
          </div>
          <p v-if="child.groups.length > 0" class="my-5">Assigned to groups</p>
          <div class="rounded bg-primary-light">
            <div
              class="p-4 border-0 border-solid border-gray"
              :class="index !== 0 ? 'border-t' : ''"
              v-for="(g, index) in child.groups"
              :key="'var' + index"
            >
              <span class="text-primary">{{ g.name }}</span>
            </div>
          </div>
        </div>
      </template>
    </expandabel-table>
  </view-wrapper>
</template>

<script>
import GrafokettButton from "../components/forms/Button.vue";
import ExpandabelTable from "../components/ExpandabelTable";
import Modal from "../components/Modal";
import { formatDate } from "../utils/dateHelpers";
import { getPreference, setPreference } from "@/utils/userPreferences";

export default {
  components: {
    Modal,
    GrafokettButton,
    ExpandabelTable,
  },
  data() {
    return {
      headers: {
        name: "Label name",
        modified: "Last edited",
      },
      columnsToNotSort: ["arrow"],
      loading: false,
      loadingText: "List of labels",
      warningText: "",
      showWarning: false,
      disableCreateNewLabel: false,
      dontShowAgain: false,
      labelToDelete: null,
      confirmDeleteOpen: false,
      activeSearchText: "",
      disabledSearchText: "",
    };
  },
  computed: {
    allLabels() {
      return this.$store.getters["LABELS/LABELS"].map(l => {
        return {
          id: l.labelId,
          name: l.name,
          disabled: l.disabled,
          modified: formatDate(l.modified),
          expandableData: {
            variabels: l.variables.map(v => {
              return {
                name: v.name,
                description: v.propertyDescription,
                length: v.propertySize,
                IsFixedLength: v.propertySize !== 0,
                locked: !v.editable,
                boundToName: !v.boundToId
                  ? v.name
                  : l.variables.filter(e => e.id === v.boundToId)[0].name,
              };
            }),
            groups: l.groups,
          },
        };
      });
    },
    labels() {
      return this.allLabels.filter(
        l =>
          !l.disabled &&
          l.name.toLowerCase().indexOf(this.activeSearchText.toLowerCase()) > -1
      );
    },
    disabledLabels() {
      return this.allLabels.filter(
        l =>
          l.disabled &&
          l.name.toLowerCase().indexOf(this.disabledSearchText.toLowerCase()) >
            -1
      );
    },
  },
  methods: {
    closeWarningModal() {
      this.showWarning = false;
      if (this.dontShowAgain) {
        setPreference('warnings', 'hideLabelsLimitWarning', true);
      }
    },
    async getData() {
      await this.$store.dispatch("LABELS/GET_LABELS");
      const responseLabelsCount = await this.$store.$api.get(
        "/api/labels/count"
      );
      const settingsResponse = await this.$store.$api.get("/api/subscription");
      const maxLabels = settingsResponse.data.data.maxLabels;
      this.disableCreateNewLabel =
        responseLabelsCount.data.data.labelsCount < maxLabels;
      this.warningText = `You have reach the maximum labels (${maxLabels}) for your organization!`;
      
      // Check if user has chosen to hide this warning
      const hideWarning = getPreference('warnings', 'hideLabelsLimitWarning', false);
      
      this.showWarning = !this.disableCreateNewLabel && !hideWarning;
    },
    async deleteLabel() {
      this.confirmDeleteOpen = false;
      this.loading = true;
      const { data } = await this.$store.$api.get(
        `/api/labels/${this.labelToDelete.parentId}/usage`
      );
      if (!data.data.length > 0) {
        await this.$store.$api.delete(
          `/api/labels/${this.labelToDelete.parentId}`
        );
      } else {
        this.loading = false;
        this.warningText =
          "This label can't be removed because it has been printed.";
        this.showWarning = true;
      }
      this.labelToDelete = null;
      this.getData();
      this.loading = false;
    },
    openConfirmModal(labelToDelete) {
      this.confirmDeleteOpen = true;
      this.labelToDelete = labelToDelete;
    },
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    this.loading = false;
  },
};
</script>
